import { Permission } from '@lucidtech/las-sdk-browser';

import { uniqueBy } from './misc';

export const createDevPermissionsForAgent = (agentId: string): Permission[] => {
  return [
    { resourceId: 'cradl:action:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:action:*/cradl:run:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:app-client:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:connection:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:document:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:function:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:hook:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:hook:*/cradl:run:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:log:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:model:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:organization:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:prediction:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:profile:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:role:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:run:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:suggestion:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:task:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:user:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:validation:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:validation:*/cradl:task:*', action: '*', effect: 'allow' },
    { resourceId: 'las:profile:*', action: '*', effect: 'allow' },
    { resourceId: `${agentId}/cradl:run:*`, action: '*', effect: 'allow' },
    { resourceId: agentId, action: '*', effect: 'allow' },
  ];
};

export const createReviewerPermissionsForAgent = (agentId: string): Permission[] => {
  return [
    { resourceId: 'cradl:action:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:action:*/cradl:run:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:document:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:function:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:hook:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:hook:*/cradl:run:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:model:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:organization:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:prediction:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:profile:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:role:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:run:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:suggestion:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:task:*', action: '*', effect: 'allow' },
    { resourceId: 'cradl:user:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:validation:*', action: 'read', effect: 'allow' },
    { resourceId: 'cradl:validation:*/cradl:task:*', action: '*', effect: 'allow' },
    { resourceId: 'las:profile:*', action: '*', effect: 'allow' },
    { resourceId: `${agentId}/cradl:run:*`, action: '*', effect: 'allow' },
    { resourceId: agentId, action: 'read', effect: 'allow' },
  ];
};

export const createPermissions = (accessLevel: AccessLevel, agentIds: string[]): Permission[] => {
  if (accessLevel === AccessLevel.Admin) {
    return [
      { resourceId: '*', action: '*', effect: 'allow' },
      { resourceId: '*/*', action: '*', effect: 'allow' },
    ];
  }

  const result = [];
  for (const agentId of agentIds) {
    if (accessLevel === AccessLevel.Developer) {
      result.push(...createDevPermissionsForAgent(agentId));
    } else if (accessLevel === AccessLevel.Reviewer) {
      result.push(...createReviewerPermissionsForAgent(agentId));
    }
  }

  return uniqueBy(
    result,
    (lhs, rhs) => lhs.action === rhs.action && lhs.effect === rhs.effect && lhs.resourceId === rhs.resourceId
  );
};

export enum AccessLevel {
  Admin = 'admin',
  Developer = 'developer',
  Reviewer = 'reviewer',
}

export const getAccessLevel = (permissions: Permission[] | null, agentId?: string): AccessLevel | null => {
  const isAdmin = permissions?.every(
    (p) => ['*/*', '*'].includes(p.resourceId) && p.action === '*' && p.effect === 'allow'
  );

  const isDeveloper = permissions?.some(
    (p) =>
      (agentId ? [agentId, 'cradl:agent:*'].includes(p.resourceId) : p.resourceId.startsWith('cradl:agent')) &&
      p.action === '*' &&
      p.effect === 'allow'
  );

  const isReviewer = permissions?.some(
    (p) =>
      (agentId ? [agentId, 'cradl:agent:*'].includes(p.resourceId) : p.resourceId.startsWith('cradl:agent')) &&
      p.action === 'read' &&
      p.effect === 'allow'
  );

  if (isAdmin) {
    return AccessLevel.Admin;
  } else if (isDeveloper) {
    return AccessLevel.Developer;
  } else if (isReviewer) {
    return AccessLevel.Reviewer;
  }

  return null;
};
